/**
 * Author: Andrey Morozov
 * Confidential. Proprietary and confidential. Unauthorized copying or distribution is prohibited.
 */

/**
 * eventMapping — adapts the gateway's flat audit trail (`AgentEvent[]`) to the
 * shapes the @iam/design-system agent components render.
 *
 * The gateway emits one chronological, append-only stream of events. The
 * console's UI components, by contrast, want:
 *
 *   - ToolCallTimeline → a list of `ToolCall` rows, each pairing a `tool_call`
 *     event with its `tool_result` (or marking it running / requires_approval);
 *   - CostMeter / metrics strip → aggregated tokens, cost and call counts;
 *   - ApprovalPrompt → the pending `ApprovalRequest`s of a session.
 *
 * This module is the single, tested translation layer between the two.
 */

import type { ToolCall, ToolCallStatus } from "@iam/design-system/agent";
import type { ApprovalRequest } from "@iam/design-system/agent";
import type { AgentEvent, SessionView } from "./types";

/** A model thought or final response extracted from the trail. */
export type TimelineThought = {
  id: string;
  kind: "think" | "response";
  text: string;
  stepSeq: number;
};

/** Aggregated run metrics for the console's metrics strip. */
export type SessionMetrics = {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsd: number;
  modelCalls: number;
  toolCalls: number;
};

/** byStep returns a copy of events sorted ascending by step_seq. */
function byStep(events: AgentEvent[]): AgentEvent[] {
  return [...events].sort((a, b) => a.step_seq - b.step_seq);
}

/**
 * mapToolCalls folds a session's audit trail into ToolCallTimeline rows. A
 * `tool_call` with a matching `tool_result` is success or failed (the result
 * carries an `error_code`); with a matching `approval_request` it is
 * requires_approval; with neither it is still running.
 */
export function mapToolCalls(events: AgentEvent[]): ToolCall[] {
  const ordered = byStep(events);

  const approvalPending = new Set<string>();
  for (const e of ordered) {
    if (e.kind === "approval_request" && e.tool_name) {
      approvalPending.add(e.tool_name);
    }
    if (e.kind === "approval_decision" && e.tool_name) {
      approvalPending.delete(e.tool_name);
    }
  }

  const results = new Map<string, AgentEvent>();
  for (const e of ordered) {
    if (e.kind === "tool_result" && e.tool_call_id) {
      results.set(e.tool_call_id, e);
    }
  }

  const calls: ToolCall[] = [];
  for (const e of ordered) {
    if (e.kind !== "tool_call" || !e.tool_call_id) continue;
    const result = results.get(e.tool_call_id);
    let status: ToolCallStatus;
    if (result) {
      status = result.error_code ? "failed" : "success";
    } else if (e.tool_name && approvalPending.has(e.tool_name)) {
      status = "requires_approval";
    } else {
      status = "running";
    }
    const call: ToolCall = {
      id: e.tool_call_id,
      name: e.tool_name ?? "(unknown tool)",
      status,
      startedAt: e.at,
      input: e.input,
    };
    if (result) {
      call.endedAt = result.at;
      if (typeof result.latency_ms === "number") call.durationMs = result.latency_ms;
      if (result.output !== undefined) call.output = result.output;
      if (result.error_code) {
        call.errorMessage = result.message ?? result.error_code;
      }
    }
    calls.push(call);
  }
  return calls;
}

/** computeMetrics aggregates token usage, cost and call counts from the trail. */
export function computeMetrics(events: AgentEvent[]): SessionMetrics {
  let promptTokens = 0;
  let completionTokens = 0;
  let costUsd = 0;
  let modelCalls = 0;
  let toolCalls = 0;
  for (const e of events) {
    if (e.kind === "model_call") {
      modelCalls += 1;
      promptTokens += e.prompt_tokens ?? 0;
      completionTokens += e.completion_tokens ?? 0;
      costUsd += e.cost_usd ?? 0;
    }
    if (e.kind === "tool_call") toolCalls += 1;
  }
  return {
    promptTokens,
    completionTokens,
    totalTokens: promptTokens + completionTokens,
    costUsd,
    modelCalls,
    toolCalls,
  };
}

/**
 * mapApprovals projects the session's still-pending gateway approvals into
 * ApprovalPrompt requests. Decided approvals are dropped — the prompt only
 * renders open decisions.
 */
export function mapApprovals(session: SessionView): ApprovalRequest[] {
  return session.approvals
    .filter((a) => a.decision === "pending")
    .map((a) => ({
      id: a.id,
      action: a.action.action,
      reason: a.action.summary,
      scopes: a.scope_required ? [{ name: a.scope_required, elevated: true }] : [],
      affectedResources: a.action.effects,
      estimatedCost:
        a.action.estimated_cost_usd > 0 ? `$${a.action.estimated_cost_usd.toFixed(4)}` : undefined,
    }));
}

/** mapTimelineThoughts extracts model think + response text in step order. */
export function mapTimelineThoughts(events: AgentEvent[]): TimelineThought[] {
  return byStep(events)
    .filter((e) => (e.kind === "think" || e.kind === "response") && !!e.text)
    .map((e) => ({
      id: e.event_id,
      kind: e.kind as "think" | "response",
      text: e.text ?? "",
      stepSeq: e.step_seq,
    }));
}
